import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * RequesterPicker — type-ahead picker for the ticket's requester (the
 * person whose problem this ticket represents), with inline "create new
 * contact" fallback for phone-logged tickets where the requester isn't
 * in the system yet.
 *
 * Industry parity: ServiceNow / Freshservice / Zoho Desk / ManageEngine
 * all have this exact UX. The "look them up OR create on the fly" loop
 * is what separates a usable service desk from a frustrating one — if
 * agents have to bail mid-ticket to admin-create the requester, they
 * never put the right person on the ticket.
 *
 * Backend: reuses GET /admin/users with `search`, `client_id`,
 * `is_internal=false` filters (Phase-1 search already exists; no new
 * endpoint needed). Create uses POST /admin/users with
 * `user_type=customer`.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, Plus, User, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { ApiError, api } from '@/lib/api';
import { t, t as tr } from '@/lib/i18n';
export function RequesterPicker({ clientId, value, onChange, displayName }) {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const containerRef = useRef(null);
    // Debounce typing — don't fire a query on every keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
        return () => clearTimeout(t);
    }, [query]);
    // Close dropdown on outside click.
    useEffect(() => {
        function onClick(e) {
            if (!containerRef.current?.contains(e.target))
                setOpen(false);
        }
        if (open)
            document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);
    const search = useQuery({
        queryKey: ['requester-search', clientId, debouncedQuery],
        queryFn: () => {
            // Empty query returns first page so the dropdown isn't empty-looking
            // when the user clicks in. Filter to Customer Users for THIS client.
            const params = new URLSearchParams({
                client_id: clientId,
                is_internal: 'false',
                limit: '10',
            });
            if (debouncedQuery)
                params.set('search', debouncedQuery);
            return api.get(`/admin/users?${params.toString()}`);
        },
        enabled: !!clientId && open,
        staleTime: 30_000,
    });
    if (!clientId) {
        return (_jsx(Input, { value: "", onChange: () => { }, disabled: true, placeholder: t('Pick a client first') }));
    }
    const selectedLabel = value && displayName ? displayName : query || '';
    const showCreatePrompt = open && debouncedQuery.length >= 2 && (search.data?.items.length ?? 0) === 0;
    return (_jsxs("div", { ref: containerRef, className: "relative", children: [_jsxs("div", { className: "flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus-within:border-brand-500 dark:border-slate-700 dark:bg-slate-900", children: [_jsx(User, { size: 14, className: "shrink-0 text-slate-400" }), _jsx("input", { className: "min-w-0 flex-1 bg-transparent outline-none placeholder:text-slate-400", value: open ? query : selectedLabel, onChange: (e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                            // If user starts typing, treat as wanting to change selection
                            if (value && e.target.value !== displayName)
                                onChange('');
                        }, onFocus: () => setOpen(true), placeholder: t('Type name or email to search…') }), value && !open && (_jsx("button", { type: "button", onClick: () => {
                            onChange('');
                            setQuery('');
                        }, className: "rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800", "aria-label": t('Clear'), children: _jsx(X, { size: 12 }) })), _jsx(ChevronDown, { size: 14, className: "shrink-0 text-slate-400" })] }), open && (_jsxs("div", { className: "absolute left-0 right-0 top-full z-20 mt-1 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900", children: [search.isLoading && (_jsxs("div", { className: "flex items-center gap-2 p-3 text-xs text-slate-500", children: [_jsx(Loader2, { size: 12, className: "animate-spin" }), " Searching\u2026"] })), search.isError && (_jsxs("div", { className: "p-3 text-xs text-rose-600", children: ["Failed to search: ", search.error?.message ?? 'Unknown error'] })), !search.isLoading && (search.data?.items.length ?? 0) > 0 && (_jsx("ul", { className: "divide-y divide-slate-100 dark:divide-slate-800", children: search.data.items.map((u) => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => {
                                    onChange(u.id, u);
                                    setQuery(u.display_name);
                                    setOpen(false);
                                }, className: "block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60", children: [_jsx("div", { className: "text-sm font-medium text-slate-900 dark:text-slate-100", children: u.display_name }), _jsx("div", { className: "text-xs text-slate-500", children: u.email })] }) }, u.id))) })), showCreatePrompt && (_jsxs("div", { className: "border-t border-slate-100 p-3 text-xs dark:border-slate-800", children: [_jsx("p", { className: "mb-2 text-slate-500", children: "No matching contact found for this client." }), _jsxs(Button, { size: "sm", variant: "outline", onClick: () => {
                                    setOpen(false);
                                    setShowCreate(true);
                                }, children: [_jsx(Plus, { size: 12 }), " Create new contact \"", debouncedQuery, "\""] })] })), !showCreatePrompt && (_jsx("div", { className: "border-t border-slate-100 px-3 py-2 dark:border-slate-800", children: _jsxs("button", { type: "button", onClick: () => {
                                setOpen(false);
                                setShowCreate(true);
                            }, className: "flex w-full items-center gap-1 text-xs text-brand-700 hover:underline dark:text-brand-300", children: [_jsx(Plus, { size: 12 }), " Create a new contact for this client"] }) }))] })), showCreate && (_jsx(CreateContactModal, { clientId: clientId, initialEmail: debouncedQuery.includes('@') ? debouncedQuery : '', initialName: debouncedQuery.includes('@') ? '' : debouncedQuery, onClose: () => setShowCreate(false), onCreated: (u) => {
                    onChange(u.id, u);
                    setQuery(u.display_name);
                    setShowCreate(false);
                } }))] }));
}
function CreateContactModal({ clientId, initialEmail = '', initialName = '', onClose, onCreated, }) {
    const toast = useToast();
    const [email, setEmail] = useState(initialEmail);
    const [displayName, setDisplayName] = useState(initialName);
    const [phone, setPhone] = useState('');
    const [showErr, setShowErr] = useState(false);
    const create = useMutation({
        mutationFn: () => api.post('/admin/users', {
            email: email.trim().toLowerCase(),
            display_name: displayName.trim(),
            user_type: 'customer',
            client_id: clientId,
            phone: phone.trim() || undefined,
            role_ids: [],
        }),
        onSuccess: (u) => {
            toast.success(tr('Contact created'), `${u.email} added as a Customer User.`);
            onCreated(u);
        },
        onError: (err) => {
            toast.error(tr('Could not create contact'), err.message);
        },
    });
    const valid = email.trim().includes('@') && displayName.trim().length > 0;
    return (_jsx(Modal, { open: true, title: "Create new contact", onClose: onClose, size: "md", children: _jsxs("div", { className: "space-y-4", children: [_jsx("p", { className: "text-xs text-slate-500", children: "Creates a Customer User attached to this client. They can be invited to the portal later via Admin \u2192 Users." }), _jsx(Field, { label: t('Email'), required: true, error: showErr && !email.trim().includes('@') ? 'Valid email required' : undefined, children: _jsx(Input, { type: "email", value: email, onChange: (e) => setEmail(e.target.value), placeholder: t('person@client.com'), autoFocus: true }) }), _jsx(Field, { label: t('Display name'), required: true, error: showErr && !displayName.trim() ? 'Name is required' : undefined, children: _jsx(Input, { value: displayName, onChange: (e) => setDisplayName(e.target.value), placeholder: t('Alex Morgan') }) }), _jsx(Field, { label: t('Phone (optional)'), children: _jsx(Input, { value: phone, onChange: (e) => setPhone(e.target.value), placeholder: t('+91 …') }) }), _jsxs("div", { className: "flex justify-end gap-2 border-t border-slate-200 pt-3 dark:border-slate-800", children: [_jsx(Button, { variant: "ghost", onClick: onClose, disabled: create.isPending, children: "Cancel" }), _jsx(Button, { disabled: create.isPending, onClick: () => {
                                setShowErr(true);
                                if (!valid)
                                    return;
                                create.mutate();
                            }, children: create.isPending ? 'Creating…' : 'Create + use as requester' })] })] }) }));
}
